import { createSelector } from 'reselect';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import { selectors as commonSelectors } from '~/domains/common';
import {
  AGENT_SCANNERS_ENABLED,
  AGENT_SCANNERS_ENTITLEMENT_CHECK_ENABLED,
  AGENT_SCANNERS_MANUAL_REVIEW_ENABLED
} from '~/domains/featureFlags/keys';

/**
 * Agent Scanner Redux Selectors
 * Following the same pattern as clientApplications/selectors.js
 *
 * Currently only includes list-related selectors.
 * Additional selectors (create, update, delete, etc.) can be added as needed.
 */

export const isAgentScannersFlagEnabled = (state) =>
  featureFlagsSelectors.featureFlag(state, AGENT_SCANNERS_ENABLED);

export const isAgentScannersEntitlementCheckEnabled = (state) =>
  featureFlagsSelectors.featureFlag(
    state,
    AGENT_SCANNERS_ENTITLEMENT_CHECK_ENABLED
  );

export const isManualReviewEnabled = (state) =>
  featureFlagsSelectors.featureFlag(
    state,
    AGENT_SCANNERS_MANUAL_REVIEW_ENABLED
  );

/**
 * Returns true when the user's organization has the agentScanners entitlement enabled.
 */
export const isAgentScannersEntitled = createSelector(
  commonSelectors.profile,
  (profile) => {
    const entitlements = profile?.organization?.entitlements;
    const agentScannersEntitlement = entitlements?.agentScanners;

    const result =
      agentScannersEntitlement === true ||
      agentScannersEntitlement?.enabled === true;

    return result;
  }
);

/**
 * Get all scanners from state
 * @param {Object} state - Redux state
 * @returns {Array} Array of scanners
 */
export function scanners(state) {
  return state.agentScanners?.scanners || [];
}

/**
 * Get a specific scanner by ID
 * @param {Object} state - Redux state
 * @param {string} scannerId - Scanner ID
 * @returns {Object|null} Scanner object or null
 */
export function scanner(state, scannerId) {
  const scannersList = state.agentScanners?.scanners || [];

  return scannersList.find((s) => s.id === scannerId) || null;
}

/**
 * Check if scanners are being fetched
 * @param {Object} state - Redux state
 * @returns {boolean} True if fetching
 */
export function isFetchingScanners(state) {
  return state.agentScanners?.isFetchingScanners || false;
}

/**
 * Get agent capacity (used / total) for the organization.
 * Null when not loaded, API unavailable, or on error (UI hides capacity line).
 *
 * @param {Object} state - Redux state
 * @returns {{ used: number, total: number }|null} Capacity data for label or null
 */
export function agentCapacity(state) {
  return state.agentScanners?.agentCapacity ?? null;
}

/**
 * Get scanners error
 * @param {Object} state - Redux state
 * @returns {Object|null} Error object or null
 */
export function scannersError(state) {
  return state.agentScanners?.scannersError || null;
}

/**
 * Get pagination information
 * @param {Object} state - Redux state
 * @returns {Object} Pagination metadata
 */
export const paginationInfo = createSelector(
  (state) => state.agentScanners,
  (agentScanners) => {
    if (!agentScanners) {
      return {
        currentPage: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0,
        hasMorePages: false
      };
    }

    return {
      currentPage: agentScanners.currentPage || 0,
      pageSize: agentScanners.pageSize || 10,
      totalElements: agentScanners.totalElements || 0,
      totalPages: agentScanners.totalPages || 0,
      hasMorePages: agentScanners.hasMorePages || false
    };
  }
);

/**
 * Check if there are more pages available
 * @param {Object} state - Redux state
 * @returns {boolean} True if there are more pages
 */
export function hasMorePages(state) {
  return state.agentScanners?.hasMorePages || false;
}

/**
 * Get total number of scanners
 * @param {Object} state - Redux state
 * @returns {number} Total number of scanners
 */
export function totalScanners(state) {
  return state.agentScanners?.totalElements || 0;
}

// Scanner Runs Selectors

/**
 * Get scanner runs (history) from state
 * @param {Object} state - Redux state
 * @returns {Array} Array of scanner runs
 */
export function scannerRuns(state) {
  return state.agentScanners?.scannerRuns || [];
}

/**
 * Check if scanner runs are being fetched
 * @param {Object} state - Redux state
 * @returns {boolean} True if fetching
 */
export function isFetchingScannerRuns(state) {
  return state.agentScanners?.isFetchingScannerRuns || false;
}

/**
 * Get scanner runs error
 * @param {Object} state - Redux state
 * @returns {Object|null} Error object or null
 */
export function scannerRunsError(state) {
  return state.agentScanners?.scannerRunsError || null;
}

/**
 * Get the scanner ID for the current runs
 * @param {Object} state - Redux state
 * @returns {string|null} Scanner ID or null
 */
export function scannerRunsScannerId(state) {
  return state.agentScanners?.scannerRunsScannerId || null;
}

// Staging Agents Selectors

/**
 * Get Staging Agents from state
 * @param {Object} state - Redux state
 * @returns {Array} Array of scanner runs
 */
export function stagingAgents(state) {
  return state.agentScanners?.stagingAgents || [];
}

/**
 * Check if Staging Agents are being fetched
 * @param {Object} state - Redux state
 * @returns {boolean} True if fetching
 */
export function isFetchingStagingAgents(state) {
  return state.agentScanners?.isFetchingStagingAgents || false;
}

/**
 * Get Staging Agents error
 * @param {Object} state - Redux state
 * @returns {Object|null} Error object or null
 */
export function stagingAgentsError(state) {
  return state.agentScanners?.stagingAgentsError || null;
}

/**
 * Get the scanRun ID for the current runs
 * @param {Object} state - Redux state
 * @returns {string|null} Scanner ID or null
 */
export function stagingAgentsScanRunId(state) {
  return state.agentScanners?.stagingAgentsScanRunId || null;
}
